import axios from 'axios';
import { SETUP_TIMEOUT_MS, withSetupRetry } from './setupRequest';

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  // Send the httpOnly auth cookie on every request. Required for the
  // cookie-based JWT migration (previously stored in localStorage).
  withCredentials: true,
});

// Per-installation identity for Settings → "Where you're logged in".
// pik_web_device_id identifies a BROWSER PROFILE (localStorage-scoped), not a
// physical computer — separate profiles legitimately show as separate entries.
// device_name is the honest machine CLASS from the UA ("Mac"/"Windows
// computer"/"iPhone") — never the browser name. app_label is set server-side
// ("PeopleiKnow web app").
const WEB_VERSION = process.env.REACT_APP_VERSION || 'web';

function webDeviceId() {
  try {
    let id = localStorage.getItem('pik_web_device_id');
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem('pik_web_device_id', id);
    }
    return id;
  } catch (e) {
    return '';
  }
}

function webMachineClass() {
  const ua = (navigator && navigator.userAgent) || '';
  // Mobile browsers FIRST — iPhone Safari's UA contains "like Mac OS X", so a
  // Mac-first check would mislabel an iPhone browser as a Mac (and an Android
  // browser as a Linux computer). A browser session on a phone is still a WEB
  // session; the honest machine class is the phone, not a desktop.
  if (/iPhone|iPod/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac';
  if (/Windows NT/.test(ua)) return 'Windows computer';
  if (/CrOS/.test(ua)) return 'Chromebook';
  if (/Linux/.test(ua)) return 'Linux computer';
  return 'Computer';
}

API.interceptors.request.use((config) => {
  try {
    config.headers['X-PIK-Client'] = `web/${WEB_VERSION}`;
    config.headers['X-PIK-Device-Id'] = webDeviceId();
    config.headers['X-PIK-Device-Name'] = webMachineClass();
  } catch (e) { /* headers are best-effort — never block a request */ }
  return config;
});

// Note: We no longer attach an Authorization header from localStorage.
// The httpOnly `pik_token` cookie travels automatically.
// Legacy: any code that still reads `pik_token` from localStorage will not
// find it after this migration; use the API helpers instead.

// Track in-flight session probes so we don't pile up duplicate
// /auth/session calls when a burst of background requests fails at
// the same time. Session 126t — switched probe to the soft endpoint
// that returns 200 with `authenticated:false` instead of 401.
let _sessionProbe = null;
async function _probeSession() {
  if (_sessionProbe) return _sessionProbe;
  _sessionProbe = API.get('/auth/session')
    .then((res) => Boolean(res?.data?.authenticated))
    .catch(() => false)
    .finally(() => { _sessionProbe = null; });
  return _sessionProbe;
}

API.interceptors.response.use(
  (res) => res,
  async (err) => {
    const url = err.config?.url || '';
    // Don't redirect for routes that are themselves "is the user logged in?"
    // probes — those endpoints answer the question, they don't ask it.
    const isAuthProbe = url.includes('/auth/login') || url.includes('/auth/me')
      || url.includes('/auth/session')
      || url.includes('/auth/logout') || url.includes('/auth/register');
    if (err.response?.status === 401 && !isAuthProbe) {
      // Don't force-redirect on every transient 401. Many things can produce
      // a one-off 401 without the session actually being dead:
      //   - iOS Safari sometimes drops cookies on a background fetch when
      //     the tab returns from sleep
      //   - a brief race during boot where a data call fires before the
      //     `pik_token` cookie has been re-attached
      //   - an unrelated endpoint that itself rejects auth for non-session
      //     reasons (e.g. a stale CSRF echo)
      // Re-probe `/auth/me` once. If the session is genuinely dead, fall
      // through to the redirect. If `/auth/me` answers happily, just
      // propagate the original error and let the caller decide how to
      // react — we keep the user on the page.
      const sessionAlive = await _probeSession();
      if (sessionAlive) {
        return Promise.reject(err);
      }
      try {
        localStorage.removeItem('pik_token');
        localStorage.removeItem('pik_user');
      } catch (e) { console.warn('localStorage cleanup failed', e); }
      // Only navigate if we're not already on a public/auth page — avoid
      // ping-ponging the user if they're already on /login.
      const path = window.location.pathname || '';
      const isAlreadyOnAuth = path === '/login' || path === '/signup'
        || path.startsWith('/p/') || path.startsWith('/intro/')
        || path.startsWith('/auth/');
      if (!isAlreadyOnAuth) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

function withinSetup(extra) {
  return { timeout: SETUP_TIMEOUT_MS, ...(extra || {}) };
}

function setupGet(url, extra) {
  return withSetupRetry(() => API.get(url, withinSetup(extra)));
}

function setupPost(url, data, extra) {
  return withSetupRetry(() => API.post(url, data, withinSetup(extra)));
}

function setupPut(url, data, extra) {
  return withSetupRetry(() => API.put(url, data, withinSetup(extra)));
}

function timedGet(url, extra) {
  return API.get(url, withinSetup(extra));
}

function timedPost(url, data, extra) {
  return API.post(url, data, withinSetup(extra));
}

// Auth
export const authAPI = {
  register: (d) => setupPost('/auth/register', d),
  login: (d) => API.post('/auth/login', d),
  mfaVerify: (d) => API.post('/auth/mfa/verify', d),
  logout: () => API.post('/auth/logout'),
  logoutEverywhere: () => API.post('/auth/logout-everywhere'),
  // Session 124p — permanent self-serve account deletion. Body
  // `confirm: true` is enforced server-side; the UI also adds two
  // native confirm dialogs as a safety net.
  deleteAccount: () => API.post('/auth/delete-account', { confirm: true }),
  me: () => API.get('/auth/me'),
  // Soft session probe — 200 in both authed + not-authed cases so
  // the boot probe doesn't pollute /login's network panel with
  // expected 401s. Returns { authenticated: bool, user: dict|null }.
  session: () => timedGet('/auth/session'),
  onboard: () => API.put('/auth/onboard'),
  updateProfile: (d) => setupPut('/auth/profile', d),
  setDateFormat: (date_format) => API.put('/auth/profile/date-format', { date_format }),
  sessions: () => API.get('/auth/sessions'),
  changePassword: (d) => API.put('/auth/password', d),
  forgotPassword: (email) => API.post('/auth/forgot-password', { email }),
  // Session 122 — passwordless magic-link sign-in.
  requestMagicLink: (email) => API.post('/auth/request-magic-link', { email }),
  consumeMagicLink: (token) => API.get(`/auth/magic-link/${token}`),
  // Sharing Permissions API (session 99) — values live on the self-card
  // contact; these endpoints only manage visibility templates + the
  // default QR level. See `services/profile_sharing.py`.
  getSharing: () => API.get('/auth/sharing'),
  toggleSharingField: ({ list, level, field, enabled }) =>
    API.post('/auth/sharing/toggle', list
      ? { list, field, enabled }
      : { level, field, enabled }),
  copyMyCard: (source_my_card, operation_id) =>
    API.post('/auth/sharing/copy-my-card', { source_my_card, operation_id }),
  connectedContacts: () => API.get('/auth/sharing/connected-contacts'),
  applyNewFieldDecision: (body) => API.post('/auth/sharing/new-field-decision', body),
  // Patch 4 (2026-10-09) — Verify-now pop-up for phone/email toggles.
  shareVerifiedChannel: (field, row_id, list_name = 'connections') =>
    API.post('/auth/sharing/verified-channel', { field, row_id, list: list_name }),
  shareUnverifiedPhone: ({ field, list, value, extension = '', label = '' }) =>
    API.post('/auth/sharing/unverified-phone', { field, list, value, extension, label }),
  addMyPhone: (phone, label = 'mobile') => API.post('/auth/my-phones', { phone, label }),
  sendMyPhoneCode: (id) => API.post(`/auth/my-phones/${encodeURIComponent(id)}/send-code`),
  checkMyPhoneCode: (id, code) => API.post(`/auth/my-phones/${encodeURIComponent(id)}/check`, { code }),
  setDefaultSharingLevel: (default_level) =>
    API.put('/auth/sharing/default-level', { default_level }),
  resetPassword: (email, resetCode, newPassword) => API.post('/auth/reset-password', { email, code: resetCode, new_password: newPassword }),
  // Session 124f — "Can't access your email anymore?" recovery flow.
  recoveryStart: (payload) => API.post('/auth/recovery/start', payload),
  recoveryVerify: (payload) => API.post('/auth/recovery/verify', payload),
  recoveryComplete: (payload) => API.post('/auth/recovery/complete', payload),
  // Session 124i — Settings → My Emails (multi-email management).
  listMyEmails: () => API.get('/auth/my-emails'),
  listMyPhones: () => API.get('/auth/my-phones'),
  setChannelLabel: (kind, id, label) =>
    API.patch(`/auth/my-${kind}s/${encodeURIComponent(id)}/label`, { label }),
  addMyEmail: (email, label) => API.post('/auth/my-emails', { email, label }),
  resendMyEmailVerification: (id) => API.post(`/auth/my-emails/${id}/resend-verification`),
  // Alias used by the per-email badge on the self-contact card.
  resendEmailVerification: (id) => API.post(`/auth/my-emails/${id}/resend-verification`),
  setMyPrimaryEmail: (id, password) => API.post(`/auth/my-emails/${id}/set-primary`, { password }),
  removeMyEmail: (id) => API.delete(`/auth/my-emails/${id}`),
  flagMyEmailForReview: (id, reason) => API.post(`/auth/my-emails/${id}/flag-for-review`, { reason }),
  verifyMyEmail: (token) => timedGet(`/auth/verify-email/${token}`),
  // Session 124v — primary-email verification resend (home-page banner).
  resendPrimaryVerification: () => API.post('/auth/resend-primary-verification'),
  // Session 124i — Link-based password reset.
  forgotPasswordLink: (email) => API.post('/auth/forgot-password-link', { email }),
  verifyResetLink: (token) => API.get(`/auth/reset-link/${token}`),
  completeResetLink: (token, newPassword) => API.post(`/auth/reset-link/${token}`, { new_password: newPassword }),
  getMyContact: () => API.get('/auth/my-contact'),
  setMyContact: (contactId) => API.put(`/auth/my-contact/${contactId}`),
  bootstrapMyContact: () => API.post('/auth/my-contact/bootstrap'),
  // Preview → production self-migration. Export the caller's own account +
  // contacts as JSON; import re-creates them (with photos re-fetched from the
  // source environment) in the CURRENT account.
  exportData: () => API.get('/auth/export-data'),
  importData: (payload) => API.post('/auth/import-data', payload, { timeout: 600000 }),
  // Session 144 — restore from a raw MongoDB backup hosted at a URL
  // (used when the old deployment is unreachable). Inspect is read-only.
  migrationInspect: () => API.get('/auth/migration/inspect'),
  importMongoDump: (payload) => API.post('/auth/import-mongodb-dump', payload, { timeout: 600000 }),
};

// MFA
export const mfaAPI = {
  status: () => API.get('/auth/mfa/status'),
  // First-time setup (MFA off) needs no step-up; re-pair while enabled passes
  // { password, code } | { repair_token } (section C.4).
  totpSetup: (stepUp) => API.post('/auth/mfa/totp/setup', stepUp || {}),
  totpEnable: (code) => API.post('/auth/mfa/totp/enable', { code }),
  // Section D — record that the user saved their recovery codes (server-side).
  acknowledgeRecoveryCodes: () => API.post('/auth/mfa/recovery-codes/acknowledge'),
  // Disable + regenerate now require FRESH proof: password + a current TOTP /
  // recovery code, OR a repair_token (last-backup-code escape hatch).
  disable: (password, code, repairToken) =>
    API.post('/auth/mfa/disable', { password, code, repair_token: repairToken }),
  regenerateRecoveryCodes: (stepUp) =>
    API.post('/auth/mfa/recovery-codes/regenerate', stepUp || {}),
};

// Pikai# — internal identity (no longer used for public URLs/UI)
export const pikaiAPI = {
  lookup: (params) => API.get('/pikai/lookup', { params }),
  syncAll: () => API.post('/pikai/sync'),
};

// Profile-share — tokenized, non-enumerable public share links
export const profileShareAPI = {
  getMine: () => API.get('/profile/share/me'),
  regenerate: () => API.post('/profile/share/me/regenerate'),
  // Leftover Intros `?level=` is ignored. Public QR / vCard always follow
  // the owner's current My Card. Extra args are accepted so older callers
  // still compile; they are not forwarded.
  getByToken: (token) =>
    API.get(`/profile/share/${token}`),
  // Direct, no-auth vCard download URL (privacy-filtered server-side).
  // Callers that need iOS Contacts append /{Name}.vcf. iOS gets inline;
  // Android Chrome and desktop get an attachment.
  getPublicVCardUrl: (token) =>
    `${API.defaults.baseURL}/profile/share/${token}/vcard`,
  addToContacts: (token, payload = {}) => timedPost(`/profile/share/${token}/add-to-contacts`, payload),
  // Anon-friendly: someone scanning the QR submits their own info back to
  // the share-token owner via the unified `/s/{token}` page. Lands in the
  // owner's intake queue for review.
  submitInfo: (token, payload) => setupPost(`/profile/share/${token}/submit-info`, payload),
  // Session 113 — close the share-link/spec gaps:
  //   • pikConsent: logged-in PIK recipient 1-click symmetric save (no form).
  //   • saveToPik:  guest converts to a PIK account, owner auto-saved as contact.
  pikConsent: (token, payload = {}) => timedPost(`/profile/share/${token}/pik-consent`, payload),
  scanState: (token) => API.get(`/profile/share/${token}/scan-state`),
  recognizeConnection: (token, body = {}) => API.post(`/profile/share/${token}/recognize-connection`, body || {}),
  applyQrExtras: (token, body) => API.post(`/profile/share/${token}/qr-extras`, body),
  reloadQrExtras: (token, body = {}) => API.post(`/profile/share/${token}/qr-extras/reload`, body || {}),
  saveToPik: (token, payload) => API.post(`/profile/share/${token}/save-to-pik`, payload),
};

// Session 130 — place intelligence: type-ahead suggestions, memory-first
// GPS resolution, and remembered corrections.
export const placesAPI = {
  suggest: (q, lat, lng) => API.get('/places/suggest', { params: { q, lat, lng } }),
  resolve: (lat, lng) => API.get('/places/resolve', { params: { lat, lng } }),
  nearby: (lat, lng) => API.get('/places/nearby', { params: { lat, lng } }),
  correction: (payload) => API.post('/places/correction', payload),
};

// Introductions — two-person tokenized intros
export const introductionsAPI = {
  recent: (params) => API.get('/intros/recent', { params: params || undefined }),
  status: (token) => timedGet(`/intros/${encodeURIComponent(token)}/status`),
  cancel: (id) => API.post(`/intros/manage/${encodeURIComponent(id)}/cancel`),
  recordAction: (id, action) => API.post(`/intros/manage/${encodeURIComponent(id)}/delivery-action`, { action }),
  create: (contactAId, contactBId) => API.post('/intros', { contact_a_id: contactAId, contact_b_id: contactBId }),
  getByToken: (token) => setupGet(`/intros/${token}`),
  addToContacts: (token) => timedPost(`/intros/${token}/add-to-contacts`),
};

// Natural-language assistant (Project B) — Claude Sonnet 4.6 intent parser.
// interpret() returns a confirm-first PLAN (never writes); the client applies
// the confirmed action via contactsAPI/introductionsAPI. Shared with mobile.
export const assistantAPI = {
  interpret: (text) => API.post('/assistant/interpret', { text }, { timeout: 60000 }),
};

// Intake — OCR screenshots, email forwarding, Quick Capture
export const intakeAPI = {
  screenshot: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post('/intake/screenshot', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  confirm: (payload) => API.post('/intake/confirm', payload),
  queue: () => API.get('/intake/queue'),
  applyQueued: (qid, payload) => API.post(`/intake/queue/${qid}/apply`, payload),
  reviewQueued: (qid, target = null, confirmMatch = false) => API.post(`/intake/queue/${encodeURIComponent(qid)}/review`, { target_contact_id: target, confirm_match: !!confirmMatch }),
  decideIncomingShare: (qid, key, action) => API.post(`/intake/queue/${encodeURIComponent(qid)}/incoming-share`, { key, action }),
  discardQueued: (qid) => API.delete(`/intake/queue/${qid}`),
  dismissBanner: (qid) => API.post(`/intake/queue/${encodeURIComponent(qid)}/dismiss-banner`),
  setup: () => API.get('/intake/setup'),
  regenerateToken: () => API.post('/intake/regenerate-token'),
  regenerateEmailAlias: () => API.post('/intake/regenerate-email-alias'),
  emailStats: () => API.get('/intake/email-stats'),
  // Screenshot-capture preferences (session 126u).
  getPreferences: () => API.get('/intake/preferences'),
  setPreferences: (payload) => API.patch('/intake/preferences', payload),
  pendingCapturesCount: () => API.get('/intake/pending-captures-count'),
  // Web Share Target (Android) — the /share-target route uploads the
  // shared screenshot via the same cookie-authenticated pipeline the
  // in-app "Upload screenshot" flow uses.
  shareTargetUpload: ({ file, latitude, longitude, capturedAt }) => {
    const fd = new FormData();
    fd.append('file', file);
    if (latitude != null) fd.append('latitude', String(latitude));
    if (longitude != null) fd.append('longitude', String(longitude));
    if (capturedAt) fd.append('captured_at', capturedAt);
    return API.post('/intake/screenshot', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Sandbox endpoint for testing email-intake without DNS/Postmark wiring.
  // Runs the full extraction → queue → notification pipeline against a
  // user-supplied payload. Returns {queued, id, confidence, extracted, candidates, sandbox}.
  testEmail: (payload) => API.post('/intake/email/test', payload),
};

// Contacts
export const contactsAPI = {
  list: (params, config) => API.get('/contacts', { params, timeout: config?.timeout }),
  listPaginated: (params) => API.get('/contacts', { params }),
  get: (id) => API.get(`/contacts/${id}`),
  // Same per-contact clean up as the iOS sparkle button.
  quickTidy: (id) => API.post(`/contacts/${id}/quick-tidy`, null, { timeout: 8000 }),
  create: (d) => timedPost('/contacts', d),
  update: (id, d) => setupPut(`/contacts/${id}`, d),
  // How did you meet reminder. Writes how_we_met only, so a partial contact PUT
  // cannot blank the rest of the card.
  howWeMet: (id, howWeMet) => API.patch(`/contacts/${id}/how-we-met`, howWeMet),
  resolveRegion: (city, country = '') => API.get('/contacts/resolve-region', { params: { city, country } }),
  // Phase 4 data-cleanup — sweep already-imported contacts with a
  // duplicated name tail ("Auntie Kathy Kathy" → "Auntie Kathy").
  duplicatedNamesCount: () => API.get('/contacts/name-hygiene/duplicated-count'),
  sweepDuplicatedNames: () => API.post('/contacts/name-hygiene/sweep-duplicated'),
  // Atomic groups-only update. Used by the quick-add affordance on the read-mode
  // contact card and the Edit page. The generic `update()` validates against
  // ContactCreate (requires first_name) so sending `{groups: [...]}` alone
  // returns 422 — this endpoint avoids that footgun.
  updateGroups: (id, groups) => API.put(`/contacts/${id}/groups`, { groups }),
  delete: (id) => API.delete(`/contacts/${id}`),
  toggleFavorite: (id) => API.post(`/contacts/${id}/favorite`),
  toggleLockFavorite: (id) => API.post(`/contacts/${id}/lock-favorite`),
  bulkDelete: () => API.delete('/contacts/bulk-delete'),
  bulkDeleteSelected: (ids) => API.post('/contacts/bulk-delete-selected', ids),
  checkDuplicate: (d) => API.post('/contacts/check-duplicate', d),
  // Session 124e — given a list of just-imported contact IDs, returns
  // which of them have an email matching an existing PiK user. The
  // result feeds the "Already on PeopleiKnow" banner shown after a
  // contact import completes.
  identifyPikUsers: (contactIds) => API.post('/contacts/identify-pik-users', { contact_ids: contactIds }),
  // Session 124q — strict "Glenn's contacts ∩ PIK users" intersection,
  // returns {connected, on_pik_not_connected, counts}.
  pikConnections: () => API.get('/contacts/pik-connections'),
  // Ignore/dismiss a discovery suggestion (optionally declining an
  // inbound request when request_id is supplied).
  dismissPikConnection: (pikUserId, requestId) =>
    API.post('/contacts/pik-connections/dismiss', { pik_user_id: pikUserId, request_id: requestId || null }),
  geocodeAll: () => API.post('/contacts/geocode-all'),
  geocodeStatus: () => API.get('/contacts/geocode-all/status'),
  importCSV: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post('/contacts/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importPhone: (parsed) => API.post('/contacts/import-phone', parsed),
  uploadPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post(`/contacts/${id}/photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 20000,
    });
  },
  deletePhoto: (id) => API.delete(`/contacts/${id}/photo`),
  updatePhotoPrefs: (id, prefs) => API.patch(`/contacts/${id}/photo-prefs`, prefs),
  uploadAdditionalPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post(`/contacts/${id}/additional-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAdditionalPhoto: (id, photoUrl) => API.delete(`/contacts/${id}/additional-photo`, { params: { photo_url: photoUrl } }),
  // Single-request multi-delete used by the new Photos section (multi-
  // select Remove flow in edit mode). Avoids reliability issues from
  // firing N parallel DELETE calls.
  bulkDeleteAdditionalPhotos: (id, photoUrls) =>
    API.post(`/contacts/${id}/additional-photo/bulk-delete`, { photo_urls: photoUrls }),
  // Promote an additional photo to the primary `image_url`. The previous
  // primary (if it was a locally-uploaded blob) is pushed back into the
  // additional list so the swap is non-destructive. Used by the
  // long-press → "Set as profile pic" lightbox affordance.
  promoteAdditionalPhoto: (id, photoUrl) =>
    API.post(`/contacts/${id}/additional-photo/promote`, { photo_url: photoUrl }),
  enrich: (contactIds) => API.post('/contacts/enrich', { contact_ids: contactIds }, { timeout: 300000 }),
  enrichPreview: () => API.get('/contacts/enrich/preview'),
  enrichAllUnenriched: () => API.post('/contacts/enrich/all-unenriched', null, { timeout: 20000 }),
  enrichResume: (jobId) => API.post(`/contacts/enrich/jobs/${encodeURIComponent(jobId)}/resume`),
  enrichPipelineAll: (stages) => API.post('/contacts/enrich/pipeline-all', { stages }),
  // Session 159 — Home "Enrich contacts" chooser. body: { stages, scope?, contact_ids? }
  enrichPipeline: (body = {}) => API.post('/contacts/enrich/pipeline-all', body, { timeout: 60000 }),
  getEnrichJob: (jobId) => API.get(`/contacts/enrich/jobs/${jobId}`),
  enrichAccept: (contactId, city, state, country) => API.post('/contacts/enrich/accept', {
    contact_id: contactId, city, state, country,
  }),
  // Session 128 — name hygiene (memory-jog extraction from name fields).
  nameHygieneScan: (contactIds) =>
    API.post('/contacts/name-hygiene/scan', contactIds ? { contact_ids: contactIds } : {}),
  nameHygieneApply: (payload) => API.post('/contacts/name-hygiene/apply', payload),
  nameHygieneRevert: (contactId) => API.post('/contacts/name-hygiene/revert', { contact_id: contactId }),
  nameHygieneSkip: (payload) => API.post('/contacts/name-hygiene/skip', payload),
  getTimeline: (id, limit = 20, skip = 0) => API.get(`/contacts/${id}/timeline?limit=${limit}&skip=${skip}`),
  addTimelineEvent: (id, event) => API.post(`/contacts/${id}/timeline`, event),
  scanDuplicates: (threshold = 0.99) => API.post('/contacts/scan-duplicates', { auto_merge_threshold: threshold, review_min: 0.80 }),
  listDeleted: () => API.get('/contacts/deleted/list'),
  restoreDeleted: (id) => API.post('/contacts/deleted/restore', { id }),
  permanentlyDelete: (id) => API.post('/contacts/deleted/permanently-delete', { id }),
  getMergeHistory: () => API.get('/contacts/merge-history'),
  revertMerge: (mergeId) => API.post(`/contacts/merge-history/revert/${mergeId}`),
  connectedMergePreview: (contactIds) =>
    API.post('/contacts/connected-merge-preview', { contact_ids: contactIds }),
  mergeWithHistory: (keepId, removeIds, source = 'manual', confidence = 0, connectedReview) =>
    API.post('/contacts/merge-with-history', { keep_id: keepId, remove_ids: removeIds, source, confidence, connected_review: connectedReview }),
  generateShareToken: (id) => API.post(`/contacts/generate-share-token/${id}`),
  getSharedContact: (token) => API.get(`/contacts/shared/${token}`),
  getSharedVCardUrl: (token) => `${API.defaults.baseURL}/contacts/shared/${token}/vcard`,
  updateCreationLocation: (id, body) => API.patch(`/contacts/${id}/creation-location`, body),
  importVCF: (file) => {
    // Clone file as Blob to prevent iOS Safari "body is disturbed or locked" error.
    return file.arrayBuffer().then(buf => {
      const blob = new Blob([buf], { type: file.type || 'text/vcard' });
      const fd = new FormData();
      fd.append('file', blob, file.name);
      return API.post('/contacts/import-vcf', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
    });
  },
  // Chunked upload for large VCF files (iOS cellular resilience)
  importVCFInit: (meta) => API.post('/contacts/import-vcf/init', meta),
  importVCFChunk: (uploadId, chunkIndex, blob, fileName) => {
    const fd = new FormData();
    fd.append('file', blob, fileName);
    return API.post(`/contacts/import-vcf/chunk?upload_id=${uploadId}&chunk_index=${chunkIndex}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  importVCFFinalize: (uploadId) => API.post('/contacts/import-vcf/finalize', { upload_id: uploadId }, { timeout: 600000 }),
  importVCFStatus: (uploadId) => API.get(`/contacts/import-vcf/status?upload_id=${uploadId}`),
  createFromShared: (data) => API.post('/contacts/from-shared', data),
  overdueReminders: () => API.get('/contacts/overdue-reminders'),
  snoozeReminder: (d) => API.post('/contacts/reminder-snooze', d),
  dismissReminder: (d) => API.post('/contacts/reminder-dismiss', d),
};

// Groups
export const groupsAPI = {
  list: () => API.get('/groups'),
  get: (id) => API.get(`/groups/${id}`),
  create: (d) => timedPost('/groups', d),
  update: (id, d) => API.put(`/groups/${id}`, d),
  delete: (id, deleteMembers = false) => API.delete(`/groups/${id}`, { params: { delete_members: deleteMembers } }),
  addMember: (gid, cid) => API.post(`/groups/${gid}/members/${cid}`),
  addMembers: (gid, contactIds) => API.post(`/groups/${gid}/members`, { contact_ids: contactIds }),
  removeMember: (gid, cid) => API.delete(`/groups/${gid}/members/${cid}`),
};

// User Preferences (Ordering)
export const preferencesAPI = {
  getGroupOrder: () => API.get('/preferences/group-order'),
  saveGroupOrder: (order) => API.put('/preferences/group-order', order),
  getFavoritesOrder: () => API.get('/preferences/favorites-order'),
  saveFavoritesOrder: (order) => API.put('/preferences/favorites-order', order),
  getSmallBook: () => API.get('/preferences/small-book'),
  dismissSmallBook: (body) => API.post('/preferences/small-book/dismiss', body || {}),
};

// iCloud Sync
export const icloudAPI = {
  testConnection: (creds) => API.post('/icloud/test-connection', creds),
  getStatus: () => API.get('/icloud/status'),
  disconnect: () => API.delete('/icloud/disconnect'),
};

// Updates
export const updatesAPI = {
  list: (status) => API.get('/updates', { params: { status } }),
  approve: (id, opts) => API.post(`/updates/${id}/approve`, null, {
    params: opts && opts.proposalVersion != null ? { proposal_version: opts.proposalVersion } : undefined,
  }),
  reject: (id, opts) => API.post(`/updates/${id}/reject`, null, {
    params: opts && opts.proposalVersion != null ? { proposal_version: opts.proposalVersion } : undefined,
  }),
  compare: (id) => API.get(`/updates/${id}/compare`),
  review: (id) => API.get(`/updates/${id}/review`),
  merge: (id, keepFields) => API.post(`/updates/${id}/merge`, { keep_fields: keepFields }),
  resolveNew: (id, action, pikId) => API.post(`/updates/${id}/resolve-new`, {
    action,
    pik_id: pikId || undefined,
  }),
  // Save the user's manually-corrected values from the review dialog, then
  // approve the update (name-hygiene "Edit" flow).
  applyEdit: (id, fields) => API.post(`/updates/${id}/apply-edit`, fields),
  undo: (id) => API.post(`/updates/${encodeURIComponent(id)}/undo`),
  openIncomingShare: (id) => API.post(`/updates/${encodeURIComponent(id)}/incoming-share`),
  decideIncomingShare: (id, key, action) => API.post(`/updates/${encodeURIComponent(id)}/incoming-share/decide`, { key, action }),
  bulkReject: (ids) => API.post('/updates/bulk-reject', ids),
  bulkApprove: (ids) => API.post('/updates/bulk-approve', ids),
};

// Settings
export const settingsAPI = {
  get: () => API.get('/settings'),
  update: (d) => API.put('/settings', d),
  setIosWaitlist: (joined) => API.put('/settings/ios-waitlist', { joined }),
  setAndroidWaitlist: (joined) => API.put('/settings/android-waitlist', { joined }),
  acknowledgePhoneApps: (body) => setupPost('/settings/phone-apps-prompt', body),
};

export const configAPI = {
  iosApp: () => API.get('/config/ios-app'),
  androidApp: () => API.get('/config/android-app'),
};

export const invitesAPI = {
  betaRequest: (body) => API.post('/invites/beta-request', body),
};

// Notifications
export const notificationsAPI = {
  list: () => API.get('/notifications'),
  markRead: (id) => API.post(`/notifications/mark-read/${id}`),
  markAllRead: () => API.post('/notifications/mark-all-read'),
  generate: () => API.post('/notifications/generate'),
  remove: (id) => API.delete(`/notifications/${id}`),
  // Reverse a predictive email-typo fix (re-adds the removed address).
  undoCorrection: (id) => API.post(`/contacts/corrections/${id}/undo`),
  recentActivity: () => API.get('/notifications/recent-activity'),
  // Actionable "to approve" set (PeopleiKnow "Needs review" + Apple inbound
  // decisions) — the source of truth for the header bell badge.
  reviews: () => API.get('/notifications/reviews'),
};

// Mutual Connections
export const mutualConnectionsAPI = {
  get: (contactId) => API.get(`/mutual-connections/${contactId}`),
};

// Communications
export const communicationsAPI = {
  log: (d) => API.post('/communications', d),
  recentlyContacted: (limit = 10) => API.get('/communications/recently-contacted', { params: { limit } }),
  favoriteSuggestions: (limit = 5) => API.get('/communications/favorite-suggestions', { params: { limit } }),
  getForContact: (contactId, limit = 50, skip = 0) => API.get(`/communications/contact/${contactId}`, { params: { limit, skip } }),
  recent: (limit = 20) => API.get('/communications/recent', { params: { limit } }),
};

// Voice
export const voiceAPI = {
  transcribe: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post('/voice/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
  },
  // Record → Whisper → GPT-5.2 notes-patch suggestion for a specific contact.
  // Returns {transcript, suggested_patch, contact_id}. The frontend then asks
  // the user to confirm before appending the patch to the contact's notes.
  notesSuggest: (audioBlob, contactId, filename = 'note.webm') => {
    const fd = new FormData();
    fd.append('audio', audioBlob, filename);
    fd.append('contact_id', contactId);
    return API.post('/voice/notes-suggest', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
};

// Admin
export const adminAPI = {
  users: () => API.get('/admin/users'),
  deletedUsers: () => API.get('/admin/users/deleted'),
  restoreUser: (id) => API.post(`/admin/users/${id}/restore`),
  permanentlyDeleteUser: (id) => API.post(`/admin/users/${id}/permanently-delete`),
  resetPassword: (id) => API.post(`/admin/users/${id}/reset-password`),
  // Session 124h — Account-recovery rate-limit observability.
  recoveryLockouts: () => API.get('/admin/recovery-lockouts'),
  unlockRecoveryLockout: (email) => API.post('/admin/recovery-lockouts/unlock', { email, confirm: true }),
  // Session 124m — diagnose "login broken" tickets and self-serve fix.
  lookupUser: (email) => API.get(`/admin/lookup-user?email=${encodeURIComponent(email)}`),
  promoteEmailToPrimary: (uid, email) => API.post(`/admin/users/${uid}/promote-email-to-primary`, { email, confirm: true }),
  waitlist: (platform = 'all') => API.get('/admin/waitlist', { params: { platform } }),
  waitlistCsv: (platform = 'all') => API.get('/admin/waitlist.csv', { params: { platform }, responseType: 'text' }),
};

// Misc
export const seedAPI = () => API.post('/seed');
export const statsAPI = () => API.get('/stats');

// Session 114 — Cross-user auto-sync consent.
// Both sides of a consent_request can list it, accept it or decline it
// from the new /consents page (linked from the bell notification).
export const connectionRequestCopy = {
  rowSubtitle: 'Wants to connect · Tap to see details',
  title: (fullName) => `${fullName} wants to connect`,
  heading: (first) => `What ${first} shares with connections`,
  empty: (first) => `${first} isn't sharing any details with connections yet.`,
  footer: (first) => `If you accept, these details are added to your contact for ${first} and stay up to date.`,
  decline: 'Decline',
  accept: 'Accept',
  seeDetails: 'See details',
  hideDetails: 'Hide details',
  editContact: 'Edit contact',
  useTheirs: 'Use theirs',
};

export const usersAPI = {
  search: (q) => API.get('/users/search', { params: { q } }),
};

export const consentRequestsAPI = {
  list: () => API.get('/consent-requests'),
  preview: (id) => API.get(`/consent-requests/${id}/preview`),
  accept: (id, where) => API.post(`/consent-requests/${id}/accept`, where || {}),
  decline: (id) => API.post(`/consent-requests/${id}/decline`),
  withdraw: (id) => API.post(`/consent-requests/${id}/withdraw`),
  dismissBanner: (requestIds) => API.post('/consent-requests/banner-dismiss', { request_ids: requestIds }),
  // Layer 1 (Feb 2026) — used by ContactDetailPage to render the 3-state
  // pikai badge ("Not on PIK" / "On PIK, not connected" / "Connected").
  statusForContact: (contactId) => API.get(`/consent-requests/by-contact/${contactId}`),
  connectContact: (contactId) => API.post(`/consent-requests/connect-contact/${contactId}`),
  connectUser: (subjectUserId) => API.post(`/consent-requests/connect-user/${subjectUserId}`),
  recentAccepts: (days = 7) => API.get(`/consent-requests/recent-accepts?days=${days}`),
};

// Group Intros session. One QR for an event. The public
// endpoints (`getPublic`, `joinGuest`, `saveToPik`) are reachable without
// auth — useful for guests at an event scanning the QR.
export const groupShareSessionAPI = {
  create: (body) => API.post('/group-share-sessions', body),
  getPublic: (token) => API.get(`/group-share-sessions/${token}`),
  // No-auth, richer server-generated vCard for a session participant (guest
  // or PeopleiKnow user). Served with Content-Disposition attachment.
  getParticipantVCardUrl: (token, participantId) =>
    `${API.defaults.baseURL}/group-share-sessions/${token}/participants/${participantId}/vcard`,
  joinGuest: (token, body) => API.post(`/group-share-sessions/${token}/join-guest`, body),
  joinPik: (token) => API.post(`/group-share-sessions/${token}/join-pik`),
  attachParticipant: (token, participantId) => timedPost(
    `/group-share-sessions/${token}/attach-participant/${encodeURIComponent(participantId)}`,
  ),
  end: (token) => API.post(`/group-share-sessions/${token}/end`),
  rename: (token, name) => API.patch(`/group-share-sessions/${token}/rename`, { name }),
  setPhotoSharing: (token, shareWithMembers) => API.patch(
    `/group-share-sessions/${token}/share-photo`,
    { share_with_members: shareWithMembers },
  ),
  setPhoto: (token, photoUrl) => API.patch(`/group-share-sessions/${token}/photo`, { photo_url: photoUrl }),
  setLocation: (token, body) => API.patch(`/group-share-sessions/${token}/location`, body),
  inviteManual: (token, body) => API.post(`/group-share-sessions/${token}/invite-manual`, body),
  saveToPik: (token, body) => API.post(`/group-share-sessions/${token}/save-to-pik`, body),
  mySessions: () => API.get('/group-share-sessions/me'),
};

// Unified onboarding — session 126
export const onboardingAPI = {
  status: () => setupGet('/onboarding/status'),
  // A places lookup. Callers must keep going when this fails.
  detectLocation: (lat, lng) => timedPost('/onboarding/location/detect', { lat, lng }, { timeout: 8000 }),
  searchLocation: (q, countryCode) => {
    const params = new URLSearchParams({ q: q || '' });
    // A picked country narrows Nominatim. Locale, GPS, and scan do not.
    if (countryCode) params.set('country_code', countryCode);
    return timedGet(`/onboarding/location/search?${params.toString()}`, { timeout: 8000 });
  },
  states: (countryCode) => timedGet(`/onboarding/location/states?country_code=${encodeURIComponent(countryCode || 'US')}`),
  confirmLocation: (body) => setupPost('/onboarding/location/confirm', body),
  locationHint: () => API.get('/onboarding/location/hint', { timeout: 4000 }),
  skipLocation: () => setupPost('/onboarding/location/skip'),
  acceptTerms: () => setupPost('/onboarding/terms/accept'),
  skipTerms: () => setupPost('/onboarding/terms/skip'),
  deferImportToIos: () => setupPost('/onboarding/import/defer-to-ios'),
  visitPikMatches: () => setupPost('/onboarding/pik-matches/visit'),
  saveResumePath: (path) => setupPost('/onboarding/resume-path', { path }),
  resendVerification: () => timedPost('/onboarding/email/resend'),
  markStep: (step, skipped = false) => setupPost('/onboarding/mark-step', { step, skipped }),
  markOnboarded: () => setupPost('/onboarding/mark-onboarded'),
  defer: (body) => setupPost('/onboarding/defer', body),
  dismissTracker: () => setupPost('/onboarding/dismiss-tracker'),
  complete: () => timedPost('/onboarding/complete'),
};

// Session 126p — feature announcements ("What's new" banner).
export const announcementsAPI = {
  active: () => API.get('/announcements/active'),
  dismiss: (id) => API.post(`/announcements/${encodeURIComponent(id)}/dismiss`),
};

export default API;
